/**
 * @file Contributor UI — skip fuel density mandatory validation when catalog has no density for the selected mix.
 */

import type {
  FormDataResponse,
  FormField,
} from "../../../../../interfaces/formInterfaces";
import type { FuelDensityApiRow } from "../../../../../services/a1Service";
import type { CalorificValuePublic } from "../../../../../services/calorificValuesPublicService";
import {
  buildSectionFieldKey,
  hasFieldValue,
} from "./contributorFormValidation";
import {
  findCalorificValueForMix,
  findFuelDensityForMix,
  getFuelMixDropdownLabelForField,
  isFuelDensityUnitFormFieldName,
} from "./fuelMixReferenceAutofill";
import {
  discoverA3FuelFieldNames,
  isA3FuelDensityUnitField,
  isA3FuelDensityValueField,
} from "../A3/utils/a3FuelMixAutofill";

export const A1_FUEL_SECTION_CODE = "A1_FUEL_EXTRACTION";
export const A1_FUEL_MIX_FIELD = "fuelMix_extraction";

export const A2_FUEL_SECTION_CODE = "A2_FUEL_TRANSPORT";
export const A2_FUEL_MIX_FIELD = "fuelMix_01";

export const A3_FUEL_SECTION_CODE = "A3_PROCESS_FUEL_DETAILS";

/** True for numeric fuel density fields (not unit, not mix). */
export function isFuelDensityValueFieldName(fieldName: string): boolean {
  const n = String(fieldName ?? "").toLowerCase();
  if (n.includes("unit") || n.includes("mix")) return false;
  return (
    n === "fueldensity_extraction" ||
    n === "fueldensity" ||
    n.includes("fuelconsumptiondensity") ||
    n.includes("processfuelconsumptiondensity") ||
    (n.includes("fueldensity") && !n.includes("mix"))
  );
}

export function isFuelDensityMandatoryField(field: FormField): boolean {
  return (
    isFuelDensityValueFieldName(field.name) ||
    isFuelDensityUnitFormFieldName(field.name)
  );
}

/** True for numeric calorific value fields (not unit, not mix). */
export function isCalorificValueFieldName(fieldName: string): boolean {
  const n = String(fieldName ?? "").toLowerCase();
  if (n.includes("unit") || n.includes("mix")) return false;
  return n.includes("calorific");
}

/** True for calorific value unit dropdowns (A1, A2, A3). */
export function isCalorificValueUnitFieldName(fieldName: string): boolean {
  const n = String(fieldName ?? "").toLowerCase();
  if (!n.includes("unit")) return false;
  return n.includes("calorific");
}

export function isCalorificValueMandatoryField(field: FormField): boolean {
  return (
    isCalorificValueFieldName(field.name) ||
    isCalorificValueUnitFieldName(field.name)
  );
}

export function isA3CalorificValueField(
  field: FormField,
  formData: FormDataResponse | null
): boolean {
  if (field.type === "UNIT") return false;
  const names = discoverA3FuelFieldNames(formData);
  return Boolean(names.calorific && field.name === names.calorific);
}

export function isA3CalorificUnitField(
  field: FormField,
  formData: FormDataResponse | null
): boolean {
  if (field.type !== "UNIT") return false;
  const names = discoverA3FuelFieldNames(formData);
  return Boolean(names.calorificUnit && field.name === names.calorificUnit);
}

/** Backend catalog row has a usable density for autofill / validation. */
export function fuelMixCatalogProvidesDensity(
  mixValue: string,
  mixDropdownLabel: string,
  catalog: FuelDensityApiRow[]
): boolean {
  if (!catalog.length) return false;
  const row = findFuelDensityForMix(mixValue, mixDropdownLabel, catalog);
  if (!row) return false;
  const m3 = String(row.densityKgM3 ?? "").trim();
  if (m3 && Number.isFinite(Number(m3))) return true;
  const liter = String(row.densityKgL ?? "").trim();
  return !!(liter && Number.isFinite(Number(liter)));
}

/** Backend catalog row has a usable calorific value for autofill / validation. */
export function fuelMixCatalogProvidesCalorificValue(
  mixValue: string,
  mixDropdownLabel: string,
  catalog: CalorificValuePublic[]
): boolean {
  if (!catalog.length) return false;
  const row = findCalorificValueForMix(mixValue, mixDropdownLabel, catalog);
  if (!row) return false;
  const cv = String(row.calorificValue ?? "").trim();
  return !!(cv && Number.isFinite(Number(cv)));
}

/** Skip required validation for fuel density + unit when mix has no catalog density. */
export function shouldSkipFuelDensityMandatoryValidation(args: {
  field: FormField;
  sectionCode: string;
  rowIndex: number;
  values: Record<string, string>;
  formData: FormDataResponse | null;
  fuelDensityCatalog: FuelDensityApiRow[];
  fuelMixFieldName: string;
  fuelMixSectionCode: string;
}): boolean {
  const {
    field,
    sectionCode,
    rowIndex,
    values,
    formData,
    fuelDensityCatalog,
    fuelMixFieldName,
    fuelMixSectionCode,
  } = args;

  if (sectionCode !== fuelMixSectionCode) return false;
  if (!isFuelDensityMandatoryField(field)) return false;

  const mixKey = buildSectionFieldKey(
    fuelMixFieldName,
    fuelMixSectionCode,
    rowIndex
  );
  const mixVal = values[mixKey] ?? "";
  if (!hasFieldValue(mixVal)) return true;

  const mixLabel = getFuelMixDropdownLabelForField(
    formData,
    fuelMixSectionCode,
    fuelMixFieldName,
    mixVal
  );
  return !fuelMixCatalogProvidesDensity(mixVal, mixLabel, fuelDensityCatalog);
}

/** Skip required validation for calorific value + unit when mix has no catalog calorific value. */
export function shouldSkipCalorificValueMandatoryValidation(args: {
  field: FormField;
  sectionCode: string;
  rowIndex: number;
  values: Record<string, string>;
  formData: FormDataResponse | null;
  calorificValuesCatalog: CalorificValuePublic[];
  fuelMixFieldName: string;
  fuelMixSectionCode: string;
}): boolean {
  const {
    field,
    sectionCode,
    rowIndex,
    values,
    formData,
    calorificValuesCatalog,
    fuelMixFieldName,
    fuelMixSectionCode,
  } = args;

  if (sectionCode !== fuelMixSectionCode) return false;
  if (!isCalorificValueMandatoryField(field)) return false;

  const mixKey = buildSectionFieldKey(
    fuelMixFieldName,
    fuelMixSectionCode,
    rowIndex
  );
  const mixVal = values[mixKey] ?? "";
  if (!hasFieldValue(mixVal)) return true;

  const mixLabel = getFuelMixDropdownLabelForField(
    formData,
    fuelMixSectionCode,
    fuelMixFieldName,
    mixVal
  );
  return !fuelMixCatalogProvidesCalorificValue(
    mixVal,
    mixLabel,
    calorificValuesCatalog
  );
}

export function shouldSkipA3FuelDensityMandatoryValidation(args: {
  field: FormField;
  sectionCode: string;
  rowIndex: number;
  values: Record<string, string>;
  formData: FormDataResponse | null;
  fuelDensityCatalog: FuelDensityApiRow[];
}): boolean {
  const { field, sectionCode, rowIndex, values, formData, fuelDensityCatalog } =
    args;

  if (sectionCode !== A3_FUEL_SECTION_CODE) return false;
  if (!isA3FuelDensityValueField(field) && !isA3FuelDensityUnitField(field)) {
    return false;
  }

  const fuelNames = discoverA3FuelFieldNames(formData);
  const mixField = fuelNames.mix;
  if (!mixField) return false;

  return shouldSkipFuelDensityMandatoryValidation({
    field,
    sectionCode,
    rowIndex,
    values,
    formData,
    fuelDensityCatalog,
    fuelMixFieldName: mixField,
    fuelMixSectionCode: A3_FUEL_SECTION_CODE,
  });
}

export function shouldSkipA3CalorificValueMandatoryValidation(args: {
  field: FormField;
  sectionCode: string;
  rowIndex: number;
  values: Record<string, string>;
  formData: FormDataResponse | null;
  calorificValuesCatalog: CalorificValuePublic[];
}): boolean {
  const {
    field,
    sectionCode,
    rowIndex,
    values,
    formData,
    calorificValuesCatalog,
  } = args;

  if (sectionCode !== A3_FUEL_SECTION_CODE) return false;
  if (
    !isA3CalorificValueField(field, formData) &&
    !isA3CalorificUnitField(field, formData)
  ) {
    return false;
  }

  const fuelNames = discoverA3FuelFieldNames(formData);
  const mixField = fuelNames.mix;
  if (!mixField) return false;

  return shouldSkipCalorificValueMandatoryValidation({
    field,
    sectionCode,
    rowIndex,
    values,
    formData,
    calorificValuesCatalog,
    fuelMixFieldName: mixField,
    fuelMixSectionCode: A3_FUEL_SECTION_CODE,
  });
}

/** Whether to show the mandatory asterisk (aligned with save validation). */
export function resolveContributorFieldMandatory(args: {
  field: FormField;
  sectionCode: string;
  rowIndex: number;
  values: Record<string, string>;
  formData?: FormDataResponse | null;
  fuelDensityCatalog?: FuelDensityApiRow[];
  calorificValuesCatalog?: CalorificValuePublic[];
}): boolean {
  const {
    field,
    sectionCode,
    rowIndex,
    values,
    formData = null,
    fuelDensityCatalog = [],
    calorificValuesCatalog = [],
  } = args;

  if (!field.isMandatory) return false;

  if (sectionCode === A3_FUEL_SECTION_CODE) {
    if (
      shouldSkipA3FuelDensityMandatoryValidation({
        field,
        sectionCode,
        rowIndex,
        values,
        formData,
        fuelDensityCatalog,
      })
    ) {
      return false;
    }
    if (
      shouldSkipA3CalorificValueMandatoryValidation({
        field,
        sectionCode,
        rowIndex,
        values,
        formData,
        calorificValuesCatalog,
      })
    ) {
      return false;
    }
  }

  if (
    sectionCode === A1_FUEL_SECTION_CODE ||
    sectionCode === A2_FUEL_SECTION_CODE
  ) {
    const mixField =
      sectionCode === A1_FUEL_SECTION_CODE
        ? A1_FUEL_MIX_FIELD
        : A2_FUEL_MIX_FIELD;
    if (
      shouldSkipFuelDensityMandatoryValidation({
        field,
        sectionCode,
        rowIndex,
        values,
        formData,
        fuelDensityCatalog,
        fuelMixFieldName: mixField,
        fuelMixSectionCode: sectionCode,
      })
    ) {
      return false;
    }
    if (
      shouldSkipCalorificValueMandatoryValidation({
        field,
        sectionCode,
        rowIndex,
        values,
        formData,
        calorificValuesCatalog,
        fuelMixFieldName: mixField,
        fuelMixSectionCode: sectionCode,
      })
    ) {
      return false;
    }
  }

  return true;
}
